import { useState, useCallback, useRef, useEffect } from 'react'
import { useSettingsStore } from '@/stores'
import {
  DEFAULT_STREAMING_MODEL_ID,
  type PronunciationDictionaryLocator,
} from '@/services/ElevenLabsStreamingService'

interface UseTTSOptions {
  voiceId?: string
  /** ElevenLabs model ID (persona voice_config.model_id); default eleven_flash_v2_5 */
  modelId?: string
  /** Pronunciation dictionary locators for ElevenLabs TTS */
  pronunciationDictionaryLocators?: PronunciationDictionaryLocator[]
  onStart?: () => void
  onEnd?: () => void
  onError?: (error: Error) => void
}

interface UseTTSReturn {
  /** Whether TTS is enabled */
  isEnabled: boolean
  /** Toggle TTS on/off */
  setEnabled: (enabled: boolean) => void
  /** Whether currently speaking */
  isSpeaking: boolean
  /** Speak the given text */
  speak: (text: string) => Promise<void>
  /** Stop speaking */
  stop: () => void
  /** Whether the provider is ready (API key configured) */
  isReady: boolean
  /** Available voices */
  voices: VoiceOption[]
  /** Currently selected voice ID */
  selectedVoiceId: string | null
  /** Set the voice to use */
  setVoiceId: (voiceId: string) => void
}

interface VoiceOption {
  id: string
  name: string
  previewUrl?: string
}

const ELEVENLABS_API_BASE = 'https://api.elevenlabs.io/v1'

/**
 * Hook for Text-to-Speech functionality using ElevenLabs API.
 * Provides simple TTS that can speak assistant messages independently.
 */
export function useTextToSpeech(options: UseTTSOptions = {}): UseTTSReturn {
  const { onStart, onEnd, onError, modelId, pronunciationDictionaryLocators } = options

  const { getElevenLabsKey, hasElevenLabsKey } = useSettingsStore()

  const [isEnabled, setIsEnabled] = useState(false)
  const [isSpeaking, setIsSpeaking] = useState(false)
  const [voices, setVoices] = useState<VoiceOption[]>([])
  const [selectedVoiceId, setSelectedVoiceId] = useState<string | null>(
    options.voiceId || null
  )

  const audioRef = useRef<HTMLAudioElement | null>(null)
  const abortControllerRef = useRef<AbortController | null>(null)

  // Track if we've already fetched voices to prevent repeated fetches
  const voicesFetchedRef = useRef(false)

  // Fetch available voices when API key is available (only once)
  useEffect(() => {
    // Prevent multiple fetches
    if (voicesFetchedRef.current) return

    const fetchVoices = async () => {
      const apiKey = getElevenLabsKey()
      if (!apiKey) return

      voicesFetchedRef.current = true

      try {
        const response = await fetch(`${ELEVENLABS_API_BASE}/voices`, {
          headers: {
            'xi-api-key': apiKey,
          },
        })

        if (!response.ok) {
          voicesFetchedRef.current = false // Allow retry on error
          throw new Error(`Failed to fetch voices: ${response.statusText}`)
        }

        const data = await response.json()
        const voiceList: VoiceOption[] = (data.voices || []).map(
          (voice: { voice_id: string; name: string; preview_url?: string }) => ({
            id: voice.voice_id,
            name: voice.name,
            previewUrl: voice.preview_url,
          })
        )

        setVoices(voiceList)

        // Set default voice if none selected (check current state to avoid stale closure)
        setSelectedVoiceId((current) => {
          if (!current && voiceList.length > 0) {
            return voiceList[0].id
          }
          return current
        })
      } catch (error) {
        console.error('[TTS] Failed to fetch voices:', error)
      }
    }

    if (hasElevenLabsKey) {
      fetchVoices()
    }
  }, [hasElevenLabsKey, getElevenLabsKey])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stop()
    }
  }, [])

  // Callbacks through refs, so a caller's inline arrows never change speak().
  const onStartRef = useRef(onStart)
  const onEndRef = useRef(onEnd)
  const onErrorRef = useRef(onError)
  useEffect(() => {
    onStartRef.current = onStart
    onEndRef.current = onEnd
    onErrorRef.current = onError
  }, [onStart, onEnd, onError])

  // Each speak() is one generation; stop() and the next speak() move it on,
  // so a stopped or replaced speak() reports nothing (SA-1435).
  const generationRef = useRef(0)

  const stop = useCallback(() => {
    generationRef.current++
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
      abortControllerRef.current = null
    }

    if (audioRef.current) {
      audioRef.current.pause()
      audioRef.current.currentTime = 0
      audioRef.current = null
    }

    setIsSpeaking(false)
  }, [])

  /**
   * Speak the text. `onStart` fires when its audio begins playing, and
   * `onEnd` fires once for every way this call ends that `stop()` or a later
   * speak() did not cut short, whether anything played or not (SA-1435).
   */
  const speak = useCallback(
    async (text: string) => {
      // Cancel any existing speech; this call is the new generation
      stop()
      const generation = generationRef.current
      const current = () => generation === generationRef.current
      let ended = false
      const end = () => {
        if (ended || !current()) return
        ended = true
        setIsSpeaking(false)
        onEndRef.current?.()
      }

      if (!isEnabled || !text.trim()) {
        end()
        return
      }

      const apiKey = getElevenLabsKey()
      if (!apiKey) {
        onErrorRef.current?.(new Error('ElevenLabs API key not configured'))
        end()
        return
      }

      const voiceId = selectedVoiceId
      if (!voiceId) {
        onErrorRef.current?.(new Error('No voice selected'))
        end()
        return
      }

      try {
        setIsSpeaking(true)

        abortControllerRef.current = new AbortController()

        const response = await fetch(
          `${ELEVENLABS_API_BASE}/text-to-speech/${voiceId}/stream`,
          {
            method: 'POST',
            headers: {
              'xi-api-key': apiKey,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              text,
              model_id: modelId ?? DEFAULT_STREAMING_MODEL_ID,
              voice_settings: {
                stability: 0.5,
                similarity_boost: 0.75,
                style: 0,
                use_speaker_boost: true,
              },
              ...(pronunciationDictionaryLocators?.length ? {
                pronunciation_dictionary_locators: pronunciationDictionaryLocators,
              } : {}),
            }),
            signal: abortControllerRef.current.signal,
          }
        )

        if (!response.ok) {
          throw new Error(`TTS request failed: ${response.statusText}`)
        }

        // Get audio blob and play it
        const blob = await response.blob()
        if (!current()) return
        const url = URL.createObjectURL(blob)

        const audio = new Audio(url)
        audioRef.current = audio
        audio.onended = () => {
          URL.revokeObjectURL(url)
          end()
        }
        audio.onerror = () => {
          URL.revokeObjectURL(url)
          if (!current()) return
          onErrorRef.current?.(new Error('Audio playback failed'))
          end()
        }

        await audio.play()
        if (current() && !ended) onStartRef.current?.()
      } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') {
          // Cancelled, not an error
          return
        }
        if (!current()) return
        console.error('[TTS] Speech error:', error)
        if (!ended) onErrorRef.current?.(error instanceof Error ? error : new Error('TTS failed'))
        end()
      }
    },
    [isEnabled, getElevenLabsKey, selectedVoiceId, modelId, pronunciationDictionaryLocators, stop]
  )

  return {
    isEnabled,
    setEnabled: setIsEnabled,
    isSpeaking,
    speak,
    stop,
    isReady: hasElevenLabsKey,
    voices,
    selectedVoiceId,
    setVoiceId: setSelectedVoiceId,
  }
}
